import { useEffect, useRef, useState } from 'react';
import edgeService from '../services/edge.service';

// VSNP-700 follow-up: when an edge transitions offline → online, the
// Configuration Summary and VPP Status panels render with stale snapshot
// data until the orchestrator receives a fresh INFORM from the edge.
// This hook surfaces a "configuration is stale" banner during that
// catch-up window AND nudges the agent to publish a fresh status (via
// the existing /status/request endpoint) instead of waiting for the
// next scheduled inform.
//
// Behaviour:
// - Only fires when isOnline === true (an offline edge legitimately has
//   stale data and is handled by the offline banner from VSNP-700).
// - Considers status stale when received_at is null OR older than
//   STALE_THRESHOLD_MS.
// - Fires edgeService.requestStatus once per (edgeId, receivedAt) pair
//   — i.e. it won't keep poking the agent on every render.
// - Polls the caller's refetch() every POLL_INTERVAL_MS while stale, so
//   the panel picks up the fresh INFORM as soon as it lands without
//   needing the user to click Refresh.
// - Stops polling after MAX_POLL_DURATION_MS to avoid indefinite traffic
//   if the agent never responds.

const STALE_THRESHOLD_MS = 30_000; // 30 s — anything older than this is "stale"
const POLL_INTERVAL_MS = 3_000;    // poll for fresh data every 3 s while stale
const MAX_POLL_DURATION_MS = 60_000; // give up polling after 60 s

interface Args {
  edgeId: string;
  isOnline: boolean;
  receivedAt: string | null | undefined;
  refetch: () => void | Promise<void>;
}

/**
 * Returns true while the caller should render the "Configuration is stale.
 * Gathering confirmation from online edge now…" banner.
 */
export function useStaleStatusRefresh({
  edgeId,
  isOnline,
  receivedAt,
  refetch,
}: Args): boolean {
  // Keep refetch in a ref so the effect doesn't tear down + recreate the
  // poll interval every render. The caller's refetch identity is rarely
  // stable (typically a fresh closure each render).
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  const [waitingForFresh, setWaitingForFresh] = useState(false);

  // Token-based de-dup so we fire requestStatus EXACTLY ONCE per stale
  // window. The token bakes the receivedAt timestamp; when fresh data
  // arrives the token changes (or the panel exits the stale window
  // entirely) and the effect re-evaluates.
  const lastRequestedTokenRef = useRef<string | null>(null);

  const ageMs = receivedAt
    ? Date.now() - new Date(receivedAt).getTime()
    : Infinity;
  const isStale = isOnline && ageMs > STALE_THRESHOLD_MS;
  const staleToken = isStale ? (receivedAt || 'no-status') : null;

  useEffect(() => {
    if (!staleToken) {
      // Either we're offline, or the data is fresh again — clear the
      // banner and reset for the next stale window.
      setWaitingForFresh(false);
      return;
    }

    if (lastRequestedTokenRef.current === staleToken) {
      // Already requested for this exact stale window; the poll from
      // the original arming effect run is still in flight.
      return;
    }
    lastRequestedTokenRef.current = staleToken;
    setWaitingForFresh(true);

    // Nudge the agent.
    edgeService.requestStatus(edgeId).catch(() => undefined);

    // Poll for fresh data; bail out after MAX_POLL_DURATION_MS so we
    // don't churn forever if the agent never replies.
    const start = Date.now();
    const poll = setInterval(() => {
      if (Date.now() - start > MAX_POLL_DURATION_MS) {
        clearInterval(poll);
        setWaitingForFresh(false);
        return;
      }
      try {
        refetchRef.current();
      } catch {
        // refetch failures are non-fatal — caller logs its own errors.
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(poll);
  }, [edgeId, staleToken]);

  return waitingForFresh;
}
